import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MESSAGE } from "../utils/message.utils";
import { setCurrentMessages } from "../utils/translate.utils";
import { LocalStorageService } from "./local-storage.service";

const DICTIONARIES: Record<string, any> = {
  fr: MESSAGE,
};

// Lazy-load EN/ES/PT : les garder hors du bundle initial (chaque dictionnaire
// + ses textes tutoriels pèse ~300-450 kB de source). Seul le FR (fallback)
// est embarqué dans main. Le chunk de la langue stockée est fetché au boot
// (constructor) et `setLanguage` recharge la page de toute façon.
const _lazyDicts: Record<string, Promise<any>> = {
  en: import("../utils/message-en.utils").then((m) => (DICTIONARIES["en"] = m.MESSAGE_EN)),
  es: import("../utils/message-es.utils").then((m) => (DICTIONARIES["es"] = m.MESSAGE_ES)),
  pt: import("../utils/message-pt.utils").then((m) => (DICTIONARIES["pt"] = m.MESSAGE_PT)),
};

const LOCALE_MAP: Record<string, string> = {
  fr: "fr-FR",
  en: "en-US",
  es: "es-ES",
  pt: "pt-BR",
};

const PAGINATOR_LABELS: Record<
  string,
  {
    itemsPerPage: string;
    nextPage: string;
    previousPage: string;
    firstPage: string;
    lastPage: string;
    of: string;
  }
> = {
  fr: {
    itemsPerPage: "Éléments par page :",
    nextPage: "Page suivante",
    previousPage: "Page précédente",
    firstPage: "Première page",
    lastPage: "Dernière page",
    of: "sur",
  },
  en: {
    itemsPerPage: "Items per page:",
    nextPage: "Next page",
    previousPage: "Previous page",
    firstPage: "First page",
    lastPage: "Last page",
    of: "of",
  },
  es: {
    itemsPerPage: "Elementos por página:",
    nextPage: "Página siguiente",
    previousPage: "Página anterior",
    firstPage: "Primera página",
    lastPage: "Última página",
    of: "de",
  },
  pt: {
    itemsPerPage: "Itens por página:",
    nextPage: "Próxima página",
    previousPage: "Página anterior",
    firstPage: "Primeira página",
    lastPage: "Última página",
    of: "de",
  },
};

@Injectable({ providedIn: "root" })
export class LanguageService {
  private localStorageService = inject(LocalStorageService);
  currentLang = signal(LanguageService.getStoredLang());

  /**
   * Combien de dictionnaires ont été posés dans `DICTIONARIES`.
   *
   * ⚠️ `DICTIONARIES` est un objet NU, peuplé après coup par le `.then` du
   * chunk paresseux — un `computed` ne peut pas le voir muter. Sans ce signal,
   * `messages` n'avait qu'une seule dépendance réactive, `currentLang` : une
   * fois évalué, il restait figé sur ce qu'il avait lu.
   *
   * Ça ne se voyait que sur le chemin de REPLI. Passé les 5 s, la callback du
   * minuteur appelle `this.messages()` — c'est la PREMIÈRE évaluation, elle
   * mémorise le français. Le chunk arrivant à 5,1 s peuplait bien
   * `DICTIONARIES["en"]`, mais `messages()` restait sur `fr` alors que
   * `TranslateUtils.translate()`, lui, basculait : interface mi-française
   * mi-anglaise, durablement. Et le `.then` remettait `_replii` à false, donc
   * le pipe recommençait à cacher du français sous la clé `en` — exactement la
   * collision que `surDictionnaireDeRepli()` a été introduit pour fermer.
   */
  private dictionnairesPoses = signal(0);

  messages = computed(() => {
    this.dictionnairesPoses();   // dépendance explicite : cf. ci-dessus
    return DICTIONARIES[this.currentLang()] ?? DICTIONARIES["fr"];
  });

  availableLanguages = [
    { code: "fr", label: "Français" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "pt", label: "Português" },
  ];

  /**
   * Promesse résolue quand le dictionnaire de la langue stockée est EN PLACE.
   *
   * Attendue par un `APP_INITIALIZER` : sans ça, l'application rendait ses
   * premiers écrans avec le dictionnaire français (seul embarqué dans le
   * bundle), puis le chunk EN/ES/PT arrivait trop tard, et `TranslateUtils`
   * (`_currentMessages`, simple variable de module) ne réaffichait rien —
   * choisir English affichait donc toute l'application en français. Constaté
   * à l'audit du 18/08/2026.
   *
   * ⚠️ Cette promesse est le seul garde-fou du problème, et elle l'est
   * PLEINEMENT depuis que le pipe `trans` est repassé `pure: true`
   * (18/08/2026). La version précédente de ce commentaire justifiait déjà
   * l'attente par la pureté du pipe — or il était alors `pure: false`
   * (04/03/2026) : ce qui rattrapait un dictionnaire arrivé en retard, c'était
   * en réalité la réévaluation permanente du pipe impur, pas cette promesse.
   * Maintenant que le pipe est pur, un dictionnaire chargé après le premier
   * rendu ne serait plus jamais réaffiché : ne pas attendre `pret` avant le
   * bootstrap ferait immédiatement revenir le défaut.
   *
   * ⚠️ Elle ne REJETTE JAMAIS. Angular fait échouer tout le bootstrap si un
   * `APP_INITIALIZER` rend une promesse rejetée : un chunk `message-en/es/pt`
   * injoignable (déploiement avec un `index.html` en cache qui pointe vers un
   * build retiré, réseau coupé, proxy d'entreprise) donnerait alors un ÉCRAN
   * BLANC à tout utilisateur non francophone. Avant l'introduction de cette
   * attente, le même incident laissait simplement l'application démarrer en
   * français : le repli du `.catch` ci-dessous restaure cette dégradation
   * douce sans rien changer au cas nominal.
   */
  readonly pret: Promise<void>;



  constructor() {
    const lang = this.currentLang();
    const lazyPromise = _lazyDicts[lang];
    if (lazyPromise) {
      // ATTENDRE, MAIS PAS INDÉFINIMENT. Le `.catch` ci-dessous ne couvre que le
      // REJET de l'import. Un chunk qui ne répond jamais — proxy d'entreprise qui
      // retient la connexion, CDN qui pend, coupure en cours de transfert — ne
      // rejette pas : la promesse ne se résout jamais, aucun `APP_INITIALIZER`
      // n'achève, et RIEN ne bootstrape. Ni le routeur, ni Sentry. Ce serait un
      // écran blanc neuf, pour tout utilisateur EN/ES/PT — c'est-à-dire
      // exactement ce que cette attente a été introduite pour supprimer.
      //
      // On borne donc l'attente. Passé ce délai on démarre en français, comme
      // avant l'introduction de l'attente ; si le dictionnaire finit par arriver,
      // son `.then` l'applique et la langue se met en place — un rattrapage tardif
      // vaut mieux qu'une session entière dans la mauvaise langue.
      const REPLI_MS = 5_000;
      const chargement = lazyPromise
        .then((dict) => {
          setCurrentMessages(dict);
          // Le dictionnaire est EN PLACE : on le signale, sinon `messages()`
          // garde ce qu'il avait lu avant (le repli français) et les gabarits
          // ne rattrapent jamais la langue.
          this.dictionnairesPoses.update((n) => n + 1);
        })
        .catch((err) => {
          // Repli sur le dictionnaire par défaut (français, embarqué dans le
          // bundle initial) : `DICTIONARIES[lang]` est resté vide, donc
          // `messages()` retombe sur `fr`. On trace, mais on ne relance pas —
          // relancer ferait échouer le bootstrap (cf. commentaire de `pret`).
          //
          // `console.error` et pas Sentry : Sentry est initialisé dans le
          // constructeur d'`AppModule`, qui s'exécute APRÈS les
          // `APP_INITIALIZER` — un `captureException` d'ici serait un no-op.
          // La sonde e2e (`fixtures/shared-session.ts`) collecte la console.
          console.error(
            `[LanguageService] dictionnaire "${lang}" injoignable — repli sur le français`,
            err,
          );
          // `true` : c'est BIEN un repli. Sans le drapeau, le pipe se remet à
          // cacher — et il cacherait du français sous la clé de la langue
          // demandée. L'effet était bénin tant que le rejet est définitif, mais
          // le drapeau mentait sur l'état réel du dictionnaire.
          setCurrentMessages(this.messages(), true);
        });
      // ⚠️ LE MINUTEUR EST DÉSARMÉ. `Promise.race` ne l'annule pas : sans
      // `clearTimeout`, sa callback s'exécute AUSSI dans le cas nominal — chunk
      // reçu en 200 ms — et écrit « toujours sans réponse » dans la console de
      // CHAQUE session EN/ES/PT. Une fausse alerte permanente, dans une console
      // que la sonde e2e collecte, sur une MR qui passe par ailleurs son temps
      // à supprimer des erreurs console (review !67).
      let minuteur: ReturnType<typeof setTimeout> | undefined;
      void chargement.finally(() => { if (minuteur) clearTimeout(minuteur); });
      this.pret = Promise.race([
        chargement,
        new Promise<void>((resoudre) =>
          minuteur = setTimeout(() => {
            // Le repli doit poser le dictionnaire, pas seulement débloquer le
            // bootstrap : sans lui on démarrerait sur un dictionnaire VIDE.
            setCurrentMessages(this.messages(), true);   // ← repli assumé
            // Le cache de traduction est GLOBAL et n'est clé que par la langue.
            // Démarrer en français avec `currentLang()` à « en » lui ferait
            // mettre du français en cache SOUS l'anglais, durablement — la
            // collision que `translate.pipe` documente comme impossible. On le
            // signale, le pipe s'abstient de cacher tant que c'est vrai.
            console.error(
              `[LanguageService] dictionnaire "${lang}" toujours sans réponse après ${REPLI_MS} ms — démarrage en français`,
            );
            resoudre();
          }, REPLI_MS),
        ),
      ]);
    } else {
      setCurrentMessages(this.messages());
      this.pret = Promise.resolve();
    }
  }

  setLanguage(lang: string, reload = true) {
    if (!DICTIONARIES[lang] && !_lazyDicts[lang]) return;
    const prevLang = this.currentLang();
    this.localStorageService.putWithTTL("justup_lang", lang);

    // `currentLang` est posé DANS TOUS LES CAS. Auparavant il ne l'était que si
    // le dictionnaire était déjà chargé — or les chunks EN/ES/PT arrivent de
    // façon asynchrone : cliquer sur une langue avant la fin du chargement
    // laissait l'application sur la précédente.
    this.currentLang.set(lang);
    if (DICTIONARIES[lang]) {
      setCurrentMessages(this.messages());
    }

    // `LOCALE_ID` est figé au bootstrap (`app.module.ts`) : sans rechargement,
    // les pipes `date` et `number` resteraient dans l'ancienne locale. Le
    // rechargement n'est donc PAS une commodité, il est structurel — et c'est
    // lui qui autorise le pipe `trans` à être `pure` (l'application ne change
    // jamais de langue à chaud). Il n'a lieu que si la langue change RÉELLEMENT.
    if (reload && prevLang !== lang) {
      window.location.reload();
    }
  }

  static getStoredLang(): string {
    const raw = localStorage.getItem("justup_lang");
    if (!raw) return "fr";
    try {
      const parsed = JSON.parse(raw);
      return parsed.value ?? raw;
    } catch {
      return raw;
    }
  }

  static getLocaleId(): string {
    return LOCALE_MAP[LanguageService.getStoredLang()] || "fr-FR";
  }
}

@Injectable({ providedIn: "root" })
export class CustomPaginatorIntl extends MatPaginatorIntl {
  constructor(private languageService: LanguageService) {
    super();
    this.applyLabels();
    effect(() => {
      this.languageService.currentLang();
      this.applyLabels();
      this.changes.next();
    });
  }

  private applyLabels() {
    const labels =
      PAGINATOR_LABELS[this.languageService.currentLang()] ??
      PAGINATOR_LABELS["fr"];
    this.itemsPerPageLabel = labels.itemsPerPage;
    this.nextPageLabel = labels.nextPage;
    this.previousPageLabel = labels.previousPage;
    this.firstPageLabel = labels.firstPage;
    this.lastPageLabel = labels.lastPage;
    this.getRangeLabel = (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) return `0 ${labels.of} ${length}`;
      const startIndex = page * pageSize;
      const endIndex = Math.min(startIndex + pageSize, Math.max(length, 0));
      return `${startIndex + 1} – ${endIndex} ${labels.of} ${length}`;
    };
  }
}
